'use client';

import { FC, useCallback, useEffect, useRef } from 'react';
import { Mxp365Dialog } from './Mxp365Dialog';
import { MXP365_EVENTS, type Mxp365OpenEvent } from './events';
import type { MxpDialogTitleCollection } from './mxp365.types';
import type PrismAPI from '@modules/prism-client/types/prism-types';

type Props = {
  titles?: MxpDialogTitleCollection;
};

export const Mxp365DialogProvider: FC<Props> = ({ titles }) => {
  // Dialog ref
  const dialogRef = useRef<{
    initiateNavigation: (
      path: string,
      title: string,
      token: string,
      queryParams?: Record<string, string>,
      telemetry?: PrismAPI.TelemetryInstruction[],
    ) => void;
  }>(null);

  // Handlers
  const handleOpenEvent = useCallback((event: Event) => {
    const customEvent = event as Mxp365OpenEvent;
    const { path, title, token, queryParams, telemetry } = customEvent.detail;

    // Navigate using the dialog's initiateNavigation with structured data
    if (dialogRef.current) {
      dialogRef.current.initiateNavigation(
        path,
        title,
        token,
        queryParams,
        telemetry,
      );
    }
  }, []);

  const handleCloseEvent = useCallback(() => {
    // The dialog manages its own open state
    // This is just for any external close triggers if needed
    if (dialogRef.current) {
      // Could trigger dialog close if needed
    }
  }, []);

  // Effects
  // Event listeners
  useEffect(() => {
    window.addEventListener(MXP365_EVENTS.OPEN, handleOpenEvent);
    window.addEventListener(MXP365_EVENTS.CLOSE, handleCloseEvent);

    return () => {
      window.removeEventListener(MXP365_EVENTS.OPEN, handleOpenEvent);
      window.removeEventListener(MXP365_EVENTS.CLOSE, handleCloseEvent);
    };
  }, [handleOpenEvent, handleCloseEvent]);

  return <Mxp365Dialog ref={dialogRef} titles={titles} />;
};
